// ============================================
// VolumeProfileSettingsMenu.jsx
// Settings popup for the Volume Profile lane.
//
// Edits vaConfig.vpBuy / vpSell / vpSide rather than owning its own state.
// Those keys already existed and are already read by the draw pass, and they
// carry behaviour worth not duplicating: a theme change resets vpBuy/vpSell
// to null so the theme's own VP colours take over again. A second config
// would silently diverge from that the first time someone switched themes.
//
// Shared menu shell (menu/ChartMenu): frame, fit, outside-click / Esc and
// styles (incl. the coarse-pointer touch sizes this menu introduced, now a
// --menu-* override for every menu) live in the CHART MENUS block.
// ============================================
import ChartMenu, { MenuRow, MenuSwatch, MenuSelect, MenuToggle, MenuDivider, MenuButton } from './menu/ChartMenu'

const SIDES = [
  { value: 'right', label: 'Right' },
  { value: 'left',  label: 'Left'  },
]

// Profile: edge-anchored dominance bars, width = row total. Footprint: the
// same rows laddered around a centre line, buy left / sell right, scaled the
// way the cells are (largest single-sided print fills a half), numbers either
// side. Same data, same rows — a render choice, not a second profile.
const MODES = [
  { value: 'profile',   label: 'Profile'   },
  { value: 'footprint', label: 'Footprint' },
]

export default function VolumeProfileSettingsMenu({ show, x, y, openLeft, config, theme, onUpdate, onClose }) {
  if (!show) return null
  const set = (patch) => onUpdate({ ...config, ...patch })

  // null means "follow the theme" — show the theme's colour so the swatch
  // reflects what is actually painted rather than a placeholder.
  const buyVal  = config.vpBuy  || theme?.volumeProfile?.buy  || '#6ea8ff'
  const sellVal = config.vpSell || theme?.volumeProfile?.sell || '#ff7b7b'
  const side = config.vpSide === 'left' ? 'left' : 'right'
  const mode = config.vpMode === 'footprint' ? 'footprint' : 'profile'
  const textOn = config.vpText !== false

  return (
    <ChartMenu anchor={{ x, y }} openLeft={openLeft} onClose={onClose}>
      <MenuRow>
        {/* Bars are coloured by which side dominated that price, so the two
            colours are the entire read — one colour would erase it. */}
        <MenuSwatch value={buyVal} onChange={(c) => set({ vpBuy: c })} title="Buy-dominant rows" />
        <MenuDivider />
        <MenuSwatch value={sellVal} onChange={(c) => set({ vpSell: c })} title="Sell-dominant rows" />
        <MenuDivider />
        <MenuSelect value={side} options={SIDES} title="Which edge the profile anchors to"
          onChange={(v) => set({ vpSide: v })} />
        <MenuDivider />
        <MenuSelect value={mode} options={MODES} title="Bars from the edge, or a footprint ladder around a centre line"
          onChange={(v) => set({ vpMode: v })} />
        {/* TEXT — only means anything in footprint mode. Disabled rather than
            hidden so the row doesn't reflow when the mode flips. */}
        <MenuToggle on={mode === 'footprint' && textOn}
          onChange={() => set({ vpText: !textOn })}
          disabled={mode !== 'footprint'}
          title={mode === 'footprint'
            ? (textOn ? 'Hide the numbers' : 'Show the numbers')
            : 'Numbers are a footprint-mode option'}>
          123
        </MenuToggle>
        <MenuDivider />
        {/* Explicit reset, because null is not reachable from a colour
            input — without it, picking any colour once permanently opts
            this chart out of theme VP colours with no way back. */}
        <MenuButton variant="ghost" title="Follow the theme's volume profile colours"
          onClick={() => set({ vpBuy: null, vpSell: null })}>
          THEME
        </MenuButton>
      </MenuRow>
    </ChartMenu>
  )
}
